/*
 * COPYRIGHT (c) 2024
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React from 'react';
import _ from 'lodash';
import { Button, Icon, Popup } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import { format } from 'date-fns';

import ElectricityIcon from '../../../images/electricIconMeters.svg';

import descriptors from '../../../locales/en-US/descriptors';
import { BaselineQualityPopupContent } from '../BaselineQualityPopup/BaselineQualityPopupContent';
import {
  IUtilityMeterBaselineDetails,
  IUtilityMeterBaselineStatus
} from '../BaselineConfigurationModels';

const getBaselineQualityComponent = (
  baselineDetails: IUtilityMeterBaselineDetails | null
): string | React.ReactNode => {
  const baselineQuality = (baselineDetails && baselineDetails?.quality) || null;
  return (
    <div>
      {baselineDetails && baselineQuality ? (
        <>
          <span>{_.capitalize(baselineQuality)}</span>
          <span className="baseline-info-icon">
            <Popup
              element={<Icon name="badge-info" root="common" size="small" />}
              on="hover"
              position="right center"
              className="baseline-quality-popup"
            >
              <BaselineQualityPopupContent baselineDetails={baselineDetails} />
            </Popup>
          </span>
        </>
      ) : (
        '--'
      )}
    </div>
  );
};

const GetActionFromBaselineDetails = (props: {
  meterId: string;
  baselineDetails: IUtilityMeterBaselineDetails | null;
  onClickAction: (meterId: string) => void;
}) => {
  const baselineDesc = descriptors.baseline;
  const [t] = useTranslation();
  const action = props.baselineDetails?.quality ? 'view' : 'create';
  return (
    <Button
      type="link"
      content={t(baselineDesc[action])}
      onClick={() => props.onClickAction(props.meterId)}
    />
  );
};

const getLastTrainedDate = (date: Date | null): string => {
  return date ? format(date, 'EEE, dd MMM yyyy') : '--';
};

const getMeterName = (meterName: string, utilityType: string) => {
  const icon =
    _.toLower(utilityType) === _.toLower('electricity') ? (
      <span>
        <img className="utility-electricity-icon" src={ElectricityIcon} />
      </span>
    ) : (
      <></>
    );
  return (
    <>
      {icon}
      <span className="utility-meter-name">{meterName}</span>
    </>
  );
};

export const generateTreeNodesforUtilityBillBaseline = (
  utilityMeters: IUtilityMeterBaselineStatus[],
  utilityTypes: string[],
  onClickAction: (meterId: string) => void
) => {
  const utilityMetersTree = [];
  for (const utilityType of utilityTypes) {
    const utilityMetersForType = utilityMeters.filter(
      (meter) => _.toLower(meter.utilityType) === _.toLower(utilityType)
    );
    if (utilityMetersForType.length > 0) {
      utilityMetersTree.push({
        data: { name: _.capitalize(utilityType) },
        key: utilityType,
        children: utilityMetersForType.map((meter) => ({
          key: meter.meterId,
          data: {
            name: getMeterName(meter.meterName, meter.utilityType),
            lastTrained: getLastTrainedDate(meter.lastTrained),
            baselineQuality: getBaselineQualityComponent(meter.baselineDetails),
            actions: (
              <GetActionFromBaselineDetails
                baselineDetails={meter.baselineDetails}
                onClickAction={onClickAction}
                meterId={meter.meterId}
              />
            )
          },
          children: []
        }))
      });
    }
  }
  return utilityMetersTree;
};
